import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  Row, Col, Card, Form, Input, Select, Button, Upload, notification,
  InputNumber, Divider, Space, Spin, Typography, Tag, Tooltip, Switch
} from 'antd';
import {
  UploadOutlined, ArrowLeftOutlined, SaveOutlined,
  PlusOutlined, DeleteOutlined, QuestionCircleOutlined, ThunderboltOutlined,
  UserOutlined, FontSizeOutlined,
  ArrowUpOutlined, ArrowDownOutlined, ArrowRightOutlined,
} from '@ant-design/icons';
import { createCampaign, updateCampaign, readCampaign, uploadPoster } from './festivalCampaignService';
import './FestivalCampaign.css';

const { Option } = Select;
const { Title, Text } = Typography;

// Load all Devanagari + general Google Fonts for the preview
if (typeof document !== 'undefined' && !document.getElementById('festival-form-fonts')) {
  const link = document.createElement('link');
  link.id = 'festival-form-fonts';
  link.rel = 'stylesheet';
  link.href = 'https://fonts.googleapis.com/css2?family=Noto+Sans+Devanagari:wght@400;700&family=Noto+Serif+Devanagari:wght@400;700&family=Baloo+2:wght@400;700&family=Hind:wght@400;700&family=Poppins:wght@400;700&family=Mukta:wght@400;800&family=Eczar:wght@400;700&family=Karma:wght@400;700&family=Biryani:wght@400;700&family=Vesper+Libre&family=Tillana:wght@400;700&family=Rajdhani:wght@400;700&family=Tiro+Devanagari+Hindi&family=Rozha+One&family=Yatra+One&family=Kalam:wght@400;700&family=Laila:wght@400;700&display=swap';
  document.head.appendChild(link);
}

// ── Font family options (available in Devanagari via Google Fonts) ────────────
const FONT_FAMILIES = [
  // ── Google Fonts (Devanagari) ──────────────────────────
  { label: 'Noto Sans Devanagari (Default)', value: 'Noto Sans Devanagari' },
  { label: 'Noto Serif Devanagari (Serif)',  value: 'Noto Serif Devanagari' },
  { label: 'Hind (Clean)',                   value: 'Hind' },
  { label: 'Mukta',                          value: 'Mukta' },
  { label: 'Mukta ExtraBold 800',            value: 'Mukta ExtraBold' },
  { label: 'Baloo 2 (Decorative)',           value: 'Baloo 2' },
  { label: 'Eczar',                          value: 'Eczar' },
  { label: 'Karma',                          value: 'Karma' },
  { label: 'Biryani',                        value: 'Biryani' },
  { label: 'Vesper Libre',                   value: 'Vesper Libre' },
  { label: 'Tillana',                        value: 'Tillana' },
  { label: 'Rajdhani',                       value: 'Rajdhani' },
  { label: 'Tiro Devanagari Hindi',          value: 'Tiro Devanagari Hindi' },
  { label: 'Rozha One',                      value: 'Rozha One' },
  { label: 'Yatra One',                      value: 'Yatra One' },
  { label: 'Kalam',                          value: 'Kalam' },
  { label: 'Laila',                          value: 'Laila' },
  // ── Windows System Fonts (Hindi) ──────────────────────
  { label: 'Kokila (Windows)',               value: 'Kokila' },
  { label: 'Aparajita (Windows)',            value: 'Aparajita' },
  { label: 'Mangal (Windows)',               value: 'Mangal' },
  { label: 'Utsaah (Windows)',               value: 'Utsaah' },
  { label: 'Sanskrit Text (Windows)',        value: 'Sanskrit Text' },
  { label: 'Gargi',                          value: 'Gargi' },
  { label: 'Lohit Devanagari',               value: 'Lohit Devanagari' },
  // ── General ───────────────────────────────────────────
  { label: 'Poppins (Modern)',               value: 'Poppins' },
  { label: 'Arial',                          value: 'Arial' },
  { label: 'Times New Roman',               value: 'Times New Roman' },
  { label: 'Georgia',                        value: 'Georgia' },
];

const DEFAULT_FONT = 'Noto Sans Devanagari';

// ── Available placeholder tokens ─────────────────────────────────────────────
const AVAILABLE_FIELDS = [
  // Member info
  { key: 'prefix',            label: 'उपसर्ग',             hint: 'Campaign prefix (आदरणीय, श्रीमान etc.)', group: 'member' },
  { key: 'name',              label: 'नाम',                hint: 'Member name',              group: 'member' },
  { key: 'relation',          label: 'रिश्ता',             hint: 'Relation (पुत्र/पुत्री)',  group: 'member' },
  { key: 'father_name',       label: 'पिता',               hint: "Father's name",            group: 'member' },
  { key: 'village',           label: 'ग्राम (स्थायी)',      hint: 'Permanent village',        group: 'member' },
  { key: 'tehsil',            label: 'तहसील (स्थायी)',      hint: 'Permanent tehsil',         group: 'member' },
  { key: 'district',          label: 'जिला (स्थायी)',       hint: 'Permanent district',       group: 'member' },
  { key: 'tem_village',       label: 'शहर (अस्थायी)',       hint: 'Temporary address city',   group: 'member' },
  { key: 'tem_district',      label: 'जिला (अस्थायी)',      hint: 'Temporary district',       group: 'member' },
  { key: 'occupation',        label: 'व्यवसाय',             hint: 'Occupation / profession',  group: 'member' },
  { key: 'address',           label: 'पूरा पता',            hint: 'Full permanent address',   group: 'member' },
  { key: 'phone',             label: 'फोन',                hint: 'Phone number',             group: 'member' },
  // Payment info
  { key: 'total_payment',     label: 'कुल भुगतान',         hint: 'Total payment (₹)',        group: 'payment' },
  { key: 'payment_count',     label: 'भुगतान संख्या',       hint: 'Number of payments',       group: 'payment' },
  { key: 'last_payment',      label: 'अंतिम भुगतान',        hint: 'Last payment amount (₹)', group: 'payment' },
  { key: 'last_payment_date', label: 'भुगतान दिनांक',       hint: 'Last payment date',        group: 'payment' },
  { key: 'receipt_no',        label: 'रसीद नं.',            hint: 'Receipt number',           group: 'payment' },
  { key: 'payment_start',     label: 'माह (आरंभ)',           hint: 'Payment start month',      group: 'payment' },
  { key: 'payment_end',       label: 'माह (अंत)',            hint: 'Payment end month',        group: 'payment' },
];

// ── Quick text template presets ───────────────────────────────────────────────
const TEXT_TEMPLATES = [
  { label: 'उपसर्ग + नाम + रिश्ता + पिता',      value: '{prefix} {name} {relation} {father_name}' },
  { label: 'नाम + रिश्ता + पिता',               value: '{name} {relation} {father_name}' },
  { label: 'ग्राम + तहसील + जिला (स्थायी)',      value: 'ग्राम {village}, तहसील {tehsil}, जिला {district}' },
  { label: 'सिर्फ ग्राम + जिला',                 value: 'ग्राम {village}, जिला {district}' },
  { label: 'अस्थायी शहर + जिला',                 value: '{tem_village}, {tem_district}' },
  { label: 'व्यवसाय',                             value: 'व्यवसाय: {occupation}' },
  { label: 'फोन नंबर',                           value: 'मो. {phone}' },
  { label: 'पूरा पता',                           value: '{address}' },
  { label: 'कुल भुगतान',                         value: 'कुल भुगतान: ₹{total_payment} ({payment_count} बार)' },
  { label: 'अंतिम भुगतान',                       value: 'अंतिम भुगतान: ₹{last_payment} दि. {last_payment_date}' },
  { label: 'भुगतान माह सीमा',                    value: 'माह: {payment_start} से {payment_end}' },
  { label: 'नाम + कुल भुगतान',                   value: '{name} — कुल भुगतान: ₹{total_payment}' },
];

// ── Sample member data for live preview ──────────────────────────────────────
const SAMPLE = {
  prefix:            'आदरणीय',
  name:              'राम लाल',
  relation:          'पुत्र',
  father_name:       'श्याम लाल',
  village:           'बड़ागाँव',
  tehsil:            'बिलाड़ा',
  district:          'जोधपुर',
  address:           'बड़ागाँव, बिलाड़ा, जोधपुर',
  tem_village:       'जयपुर',
  tem_district:      'जयपुर',
  occupation:        'किसान',
  phone:             '9876543210',
  total_payment:     '₹5,000',
  payment_count:     '5',
  last_payment:      '₹1,000',
  last_payment_date: '15 Jan 2025',
  receipt_no:        'R-1234',
  payment_start:     'Jan 2024',
  payment_end:       'Jan 2025',
};

// ── Render {token} template with sample data ──────────────────────────────────
const renderTemplate = (template, data) =>
  String(template || '').replace(/\{(\w+)\}/g, (_, key) => data[key] || '');

// ── Default rows ──────────────────────────────────────────────────────────────
const DEFAULT_ROWS = () => [
  {
    id: Date.now() + 1,
    template:    '{name} {relation} {father_name}',
    top_pct:     19.5,
    left_pct:    50,
    text_anchor: 'middle',
    font_size:   40,
    color:       '#7a0000',
    bold:        true,
    font_family: DEFAULT_FONT,
  },
  {
    id: Date.now() + 2,
    template:    'ग्राम {village}, तहसील {tehsil}, जिला {district}',
    top_pct:     23.5,
    left_pct:    50,
    text_anchor: 'middle',
    font_size:   32,
    color:       '#333333',
    bold:        false,
    font_family: DEFAULT_FONT,
  },
];

const DEFAULT_PHOTO = {
  enabled:        false,
  top_pct:        5,
  left_pct:       5,
  size_pct:       14,
  circle:         true,
  border_enabled: false,
  border_width:   4,      // pixels in actual poster
  border_color:   '#ffffff',
};

export default function FestivalCampaignForm() {
  const history  = useHistory();
  const { id }   = useParams();
  const isEdit   = Boolean(id);
  const [form]   = Form.useForm();

  const [loading, setLoading]             = useState(false);
  const [saving, setSaving]               = useState(false);
  const [uploading, setUploading]         = useState(false);
  const [posterPreview, setPosterPreview] = useState(null);
  const [posterUrl, setPosterUrl]         = useState('');
  const [posterPath, setPosterPath]       = useState('');
  const [rows, setRows]                   = useState(DEFAULT_ROWS());
  const [mediaType, setMediaType]         = useState('image');
  const [photoConfig, setPhotoConfig]     = useState(DEFAULT_PHOTO);

  // naturalWidth: actual pixel width of the base poster image (for accurate font scaling)
  const [naturalWidth, setNaturalWidth]   = useState(1080);

  const containerRef    = useRef(null);
  const draggingId      = useRef(null);
  const dragStartY      = useRef(0);
  const dragStartPct    = useRef(0);

  // Photo drag refs
  const photoDragStart  = useRef({ startX: 0, startY: 0, startLeft: 0, startTop: 0 });

  // Track focused row for placeholder insertion
  const [focusedRowId, setFocusedRowId] = useState(null);
  const inputRefs = useRef({});

  // ── Load existing campaign ────────────────────────────────────────────────
  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    readCampaign(id)
      .then((res) => {
        if (!res.status) return;
        const c  = res.result;
        const mt = c.media_type || 'image';
        setMediaType(mt);
        let tplParams = [];
        try {
          const raw = c.template_params;
          tplParams = Array.isArray(raw) ? raw : (typeof raw === 'string' ? JSON.parse(raw) : []);
        } catch (_) {}
        form.setFieldsValue({
          name:            c.name,
          prefix:          c.prefix || '',
          festival_type:   c.festival_type,
          media_type:      mt,
          template_name:   c.template_name,
          template_params: tplParams,
          status:          c.status,
        });
        if (c.poster_url)  { setPosterPreview(c.poster_url); setPosterUrl(c.poster_url); }
        if (c.poster_path) setPosterPath(c.poster_path);
        try {
          const tc = typeof c.text_config === 'string' ? JSON.parse(c.text_config) : c.text_config;
          if (tc?.rows?.length) {
            setRows(tc.rows.map((r, i) => ({
              font_family: DEFAULT_FONT,
              left_pct:    50,
              text_anchor: 'middle',
              ...r,
              id: r.id || Date.now() + i,
            })));
          } else if (tc?.row1 || tc?.row2) {
            const converted = [];
            if (tc.row1) converted.push({ id: Date.now() + 1, template: '{name} {relation} {father_name}',                        font_family: DEFAULT_FONT, left_pct: 50, text_anchor: 'middle', ...tc.row1 });
            if (tc.row2) converted.push({ id: Date.now() + 2, template: 'ग्राम {village}, तहसील {tehsil}, जिला {district}', font_family: DEFAULT_FONT, left_pct: 50, text_anchor: 'middle', ...tc.row2 });
            setRows(converted);
          }
          if (tc?.photo) setPhotoConfig({ ...DEFAULT_PHOTO, ...tc.photo });
        } catch (_) {}
      })
      .catch((err) => notification.error({ message: 'Load failed', description: err.message }))
      .finally(() => setLoading(false));
  }, [id]); // eslint-disable-line

  // ── Image load → store naturalWidth, compute preview height ──────────────
  const handleImgLoad = (e) => {
    const img = e.target;
    const nw  = img.naturalWidth || 1080;
    setNaturalWidth(nw);
    // previewHeight not needed — image drives container height via CSS height:auto
  };

  // ── Scale font size from poster px to preview px ──────────────────────────
  // Uses actual image naturalWidth so the preview is always proportionally accurate.
  const scaledFont = useCallback((fs) => {
    if (!containerRef.current) return Math.round((fs / naturalWidth) * 400);
    const containerW = containerRef.current.getBoundingClientRect().width || 400;
    return Math.max(8, Math.round((fs / naturalWidth) * containerW));
  }, [naturalWidth]);

  // ── Text row drag (X + Y) ─────────────────────────────────────────────────
  const onDragStart = useCallback((e, rowId) => {
    e.preventDefault();
    const row      = rows.find(r => r.id === rowId);
    const startX   = e.clientX ?? e.touches?.[0]?.clientX;
    const startY   = e.clientY ?? e.touches?.[0]?.clientY;
    const origTop  = row?.top_pct  ?? 0;
    const origLeft = row?.left_pct ?? 50;
    draggingId.current = rowId;

    const onMove = (ev) => {
      const cx = ev.clientX ?? ev.touches?.[0]?.clientX;
      const cy = ev.clientY ?? ev.touches?.[0]?.clientY;
      if (cx == null || !containerRef.current) return;
      const { width: w, height: h } = containerRef.current.getBoundingClientRect();
      if (!w || !h) return;
      const newTop  = Math.max(0, Math.min(97, origTop  + ((cy - startY) / h) * 100));
      const newLeft = Math.max(0, Math.min(100, origLeft + ((cx - startX) / w) * 100));
      setRows(prev => prev.map(r => r.id === rowId
        ? { ...r, top_pct: parseFloat(newTop.toFixed(1)), left_pct: parseFloat(newLeft.toFixed(1)) }
        : r
      ));
    };
    const onEnd = () => {
      draggingId.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup',   onEnd);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend',  onEnd);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup',   onEnd);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend',  onEnd);
  }, [rows]);

  // ── Photo placeholder drag ────────────────────────────────────────────────
  const onPhotoDragStart = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX ?? e.touches?.[0]?.clientX;
    const startY = e.clientY ?? e.touches?.[0]?.clientY;
    photoDragStart.current = {
      startX, startY,
      startLeft: photoConfig.left_pct,
      startTop:  photoConfig.top_pct,
    };

    const onMove = (ev) => {
      const cx = ev.clientX ?? ev.touches?.[0]?.clientX;
      const cy = ev.clientY ?? ev.touches?.[0]?.clientY;
      if (cx == null || !containerRef.current) return;
      const { width: w, height: h } = containerRef.current.getBoundingClientRect();
      const newLeft = Math.max(0, Math.min(90, photoDragStart.current.startLeft + ((cx - startX) / w) * 100));
      const newTop  = Math.max(0, Math.min(90, photoDragStart.current.startTop  + ((cy - startY) / h) * 100));
      setPhotoConfig(prev => ({
        ...prev,
        left_pct: parseFloat(newLeft.toFixed(1)),
        top_pct:  parseFloat(newTop.toFixed(1)),
      }));
    };
    const onEnd = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup',   onEnd);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend',  onEnd);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup',   onEnd);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend',  onEnd);
  }, [photoConfig]);

  // ── Row CRUD ──────────────────────────────────────────────────────────────
  const updateRow = (rowId, field, value) =>
    setRows(prev => prev.map(r => r.id === rowId ? { ...r, [field]: value } : r));

  const addRow = () =>
    setRows(prev => [...prev, {
      id:          Date.now(),
      template:    '',
      top_pct:     Math.min(93, (prev[prev.length - 1]?.top_pct ?? 30) + 6),
      left_pct:    50,
      text_anchor: 'middle',
      font_size:   32,
      color:       '#333333',
      bold:        false,
      font_family: DEFAULT_FONT,
    }]);

  const removeRow = (rowId) => setRows(prev => prev.filter(r => r.id !== rowId));

  // ── Insert placeholder token into focused row at cursor ───────────────────
  const insertPlaceholder = (key) => {
    const targetId = focusedRowId || rows[0]?.id;
    if (!targetId) return;
    const el  = inputRefs.current[targetId];
    const row = rows.find(r => r.id === targetId);
    if (!row) return;
    const pos    = el ? (el.selectionStart ?? row.template.length) : row.template.length;
    const token  = `{${key}}`;
    const newTpl = row.template.slice(0, pos) + token + row.template.slice(pos);
    updateRow(targetId, 'template', newTpl);
    requestAnimationFrame(() => {
      if (el) { el.focus(); const p = pos + token.length; el.setSelectionRange(p, p); }
    });
  };

  // ── Apply quick text template to focused/first row ────────────────────────
  const applyTextTemplate = (tplValue) => {
    const targetId = focusedRowId || rows[0]?.id;
    if (targetId) updateRow(targetId, 'template', tplValue);
  };

  // ── Upload ────────────────────────────────────────────────────────────────
  const uploadAccept = mediaType === 'image' ? 'image/*' : mediaType === 'video' ? 'video/*' : '*/*';
  const uploadLabel  = mediaType === 'image' ? 'Choose Poster Image' : mediaType === 'video' ? 'Choose Video File' : 'Choose Document File';

  const handleUpload = async (file) => {
    setUploading(true);
    try {
      if (mediaType === 'image') {
        const reader = new FileReader();
        reader.onload = (e) => setPosterPreview(e.target.result);
        reader.readAsDataURL(file);
      } else {
        setPosterPreview(null);
      }
      const res = await uploadPoster(file);
      if (res.status) {
        setPosterUrl(res.poster_url);
        setPosterPath(res.poster_path);
        notification.success({ message: 'File uploaded successfully' });
      } else {
        notification.error({ message: 'Upload failed' });
      }
    } catch (err) {
      notification.error({ message: 'Upload error', description: err.message });
    } finally {
      setUploading(false);
    }
    return false;
  };

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSubmit = async (values) => {
    if (mediaType === 'image' && !rows.length) {
      notification.warning({ message: 'Add at least one text row' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...values,
        media_type:      mediaType,
        poster_url:      posterUrl,
        poster_path:     posterPath,
        template_params: values.template_params || [],
        text_config:     { rows, photo: photoConfig },
      };
      const res = isEdit ? await updateCampaign(id, payload) : await createCampaign(payload);
      if (res.status) {
        notification.success({ message: isEdit ? 'Campaign updated' : 'Campaign created' });
        history.push('/festival-campaign');
      } else {
        notification.error({ message: res.message || 'Save failed' });
      }
    } catch (err) {
      notification.error({ message: 'Save error', description: err.message });
    } finally {
      setSaving(false);
    }
  };

  const memberFields  = AVAILABLE_FIELDS.filter(f => f.group === 'member');
  const paymentFields = AVAILABLE_FIELDS.filter(f => f.group === 'payment');

  // Photo placeholder dimensions in preview (% of container)
  const photoSizePct = photoConfig.size_pct;

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="layout-content">
      <Spin spinning={loading}>
        <Row gutter={[16, 16]}>
          <Col xs={24}>
            <Card
              title={
                <Space>
                  <Button icon={<ArrowLeftOutlined />} onClick={() => history.push('/festival-campaign')}>Back</Button>
                  <Title level={4} style={{ margin: 0 }}>
                    {isEdit ? 'Edit Campaign' : 'New Festival Campaign'}
                  </Title>
                </Space>
              }
            >
              <Form form={form} layout="vertical" onFinish={handleSubmit} initialValues={{ status: 'draft', media_type: 'image' }}>

                {/* ── Basic info ─────────────────────────────────────────── */}
                <Row gutter={16}>
                  <Col xs={24} md={7}>
                    <Form.Item name="name" label="Campaign Name" rules={[{ required: true }]}>
                      <Input placeholder="e.g. Holi 2026" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={5}>
                    <Form.Item
                      name="prefix"
                      label={
                        <Space size={4}>
                          Prefix / Salutation
                          <Tooltip title="Used as {prefix} token in templates — e.g. आदरणीय, श्रीमान, श्रीमती">
                            <QuestionCircleOutlined style={{ color: '#7a0000' }} />
                          </Tooltip>
                        </Space>
                      }
                    >
                      <Select placeholder="Select or type" allowClear showSearch mode="combobox" filterOption={false}>
                        <Option value="आदरणीय">आदरणीय</Option>
                        <Option value="श्रीमान">श्रीमान</Option>
                        <Option value="श्रीमती">श्रीमती</Option>
                        <Option value="माननीय">माननीय</Option>
                        <Option value="पूज्य">पूज्य</Option>
                        <Option value="सम्माननीय">सम्माननीय</Option>
                        <Option value="Mr.">Mr.</Option>
                        <Option value="Mrs.">Mrs.</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={4}>
                    <Form.Item name="festival_type" label="Festival Type" rules={[{ required: true }]}>
                      <Select placeholder="Select">
                        <Option value="holi">Holi</Option>
                        <Option value="diwali">Diwali</Option>
                        <Option value="newyear">Hindu New Year</Option>
                        <Option value="navratri">Navratri</Option>
                        <Option value="other">Other</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={3}>
                    <Form.Item name="media_type" label="Media Type" rules={[{ required: true }]}>
                      <Select onChange={(v) => { setMediaType(v); if (v !== 'image') setPosterPreview(null); }}>
                        <Option value="image">Image (Poster)</Option>
                        <Option value="video">Video</Option>
                        <Option value="document">Document</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={3}>
                    <Form.Item name="template_name" label="AiSensy Campaign" rules={[{ required: true }]}>
                      <Input placeholder="e.g. holi" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={2}>
                    <Form.Item name="status" label="Status">
                      <Select>
                        <Option value="draft">Draft</Option>
                        <Option value="active">Active</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                {/* ── AiSensy Template Params ──────────────────────────── */}
                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="template_params"
                      label={
                        <Space size={4}>
                          AiSensy Template Params
                          <Tooltip title={
                            <span>
                              Values sent as <b>templateParams[]</b> to AiSensy.<br />
                              Leave empty if the WhatsApp template has no <b>{'{{1}}'}</b> placeholders (e.g. image-only templates).<br />
                              Add <code>{'{name}'}</code>, <code>{'{village}'}</code> etc. to inject member data.<br />
                              Order matters — first item = {'{{1}}'}, second = {'{{2}}'} etc.
                            </span>
                          }>
                            <QuestionCircleOutlined style={{ color: '#7a0000' }} />
                          </Tooltip>
                        </Space>
                      }
                    >
                      <Select
                        mode="tags"
                        placeholder="Empty = no params. Type or pick a token e.g. {name}"
                        tokenSeparators={[',']}
                        style={{ width: '100%' }}
                      >
                        {AVAILABLE_FIELDS.map(f => (
                          <Option key={`{${f.key}}`} value={`{${f.key}}`}>
                            {`{${f.key}}`} — {f.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                {/* ── Upload ─────────────────────────────────────────────── */}
                <Row gutter={16}>
                  <Col xs={24} md={14}>
                    <Form.Item
                      label={
                        mediaType === 'image' ? 'Upload Base Poster Image'
                        : mediaType === 'video' ? 'Upload Video File'
                        : 'Upload Document File'
                      }
                    >
                      <Upload accept={uploadAccept} beforeUpload={handleUpload} showUploadList={false}>
                        <Button icon={<UploadOutlined />} loading={uploading}>
                          {uploading ? 'Uploading...' : uploadLabel}
                        </Button>
                      </Upload>
                      {posterUrl && (
                        <Text type="secondary" style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
                          {posterUrl.split('/').pop()}
                        </Text>
                      )}
                      {mediaType !== 'image' && (
                        <Text type="secondary" style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
                          {mediaType === 'video'
                            ? 'The same video will be sent to all recipients.'
                            : 'The same document will be sent to all recipients.'}
                        </Text>
                      )}
                    </Form.Item>
                  </Col>
                </Row>

                {/* ── Text rows + Photo (image type only) ──────────────── */}
                {mediaType === 'image' && (
                  <>
                    <Divider>
                      Text Row Configuration
                      <Tooltip title="Each row is a line of text overlaid on the poster. Use {placeholder} tokens. Font sizes are in poster pixels (assuming ~1080px wide poster).">
                        <QuestionCircleOutlined style={{ marginLeft: 8, color: '#7a0000' }} />
                      </Tooltip>
                    </Divider>

                    {/* ── Quick text templates ─────────────────────────── */}
                    <Card
                      size="small"
                      style={{ marginBottom: 16, background: '#f6ffed', borderColor: '#b7eb8f' }}
                      title={
                        <Space>
                          <ThunderboltOutlined style={{ color: '#52c41a' }} />
                          <Text strong style={{ color: '#389e0d' }}>Quick Text Templates</Text>
                          <Text type="secondary" style={{ fontSize: 11 }}>(applies to focused row)</Text>
                        </Space>
                      }
                    >
                      <Space wrap>
                        {TEXT_TEMPLATES.map((tpl) => (
                          <Tag
                            key={tpl.value}
                            color="green"
                            style={{ cursor: 'pointer', marginBottom: 4 }}
                            onClick={() => applyTextTemplate(tpl.value)}
                            title={tpl.value}
                          >
                            {tpl.label}
                          </Tag>
                        ))}
                      </Space>
                    </Card>

                    {/* ── Placeholder token chips ───────────────────────── */}
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ marginBottom: 6 }}>
                        <Text strong style={{ marginRight: 8, fontSize: 12 }}>Member tokens:</Text>
                        {memberFields.map((f) => (
                          <Tooltip key={f.key} title={`Insert {${f.key}} — ${f.hint}`}>
                            <Tag
                              color="blue"
                              style={{ cursor: 'pointer', marginBottom: 4 }}
                              onClick={() => insertPlaceholder(f.key)}
                            >
                              {`{${f.key}}`} {f.label}
                            </Tag>
                          </Tooltip>
                        ))}
                      </div>
                      <div>
                        <Text strong style={{ marginRight: 8, fontSize: 12 }}>Payment tokens:</Text>
                        {paymentFields.map((f) => (
                          <Tooltip key={f.key} title={`Insert {${f.key}} — ${f.hint}`}>
                            <Tag
                              color="orange"
                              style={{ cursor: 'pointer', marginBottom: 4 }}
                              onClick={() => insertPlaceholder(f.key)}
                            >
                              {`{${f.key}}`} {f.label}
                            </Tag>
                          </Tooltip>
                        ))}
                      </div>
                      <Text type="secondary" style={{ fontSize: 12, marginTop: 4, display: 'block' }}>
                        Click a token to insert at cursor in the focused row.
                      </Text>
                    </div>

                    {/* ── Row list + poster drag preview ───────────────── */}
                    <Row gutter={[16, 0]}>

                      {/* Left — row editors */}
                      <Col xs={24} md={13}>
                        {rows.map((row, idx) => (
                          <Card
                            key={row.id}
                            size="small"
                            style={{
                              marginBottom: 12,
                              border: focusedRowId === row.id ? '1px solid #7a0000' : '1px solid #e8e8e8',
                            }}
                            title={<Text strong>Row {idx + 1}</Text>}
                            extra={
                              rows.length > 1 && (
                                <Button size="small" danger icon={<DeleteOutlined />} onClick={() => removeRow(row.id)} />
                              )
                            }
                          >
                            {/* Template input */}
                            <Form.Item label="Template" style={{ marginBottom: 8 }}>
                              <Input
                                ref={el => { inputRefs.current[row.id] = el?.input || el; }}
                                value={row.template}
                                placeholder="{name} {relation} {father_name}"
                                onChange={(e) => updateRow(row.id, 'template', e.target.value)}
                                onFocus={() => setFocusedRowId(row.id)}
                                onClick={() => setFocusedRowId(row.id)}
                              />
                              {/* Inline preview in the row editor — always uses actual font */}
                              {row.template && (
                                <span
                                  className="festival-template-preview"
                                  style={{
                                    color:      row.color,
                                    fontWeight: row.bold ? 'bold' : 'normal',
                                    fontFamily: `'${row.font_family || DEFAULT_FONT}', Arial, sans-serif`,
                                    fontSize:   Math.min(22, Math.max(11, Math.round((row.font_size / naturalWidth) * 320))),
                                  }}
                                >
                                  {renderTemplate(row.template, SAMPLE)}
                                </span>
                              )}
                            </Form.Item>

                            {/* Font family selector */}
                            <Form.Item
                              label={
                                <Space size={4}>
                                  <FontSizeOutlined />
                                  Font Family
                                </Space>
                              }
                              style={{ marginBottom: 8 }}
                            >
                              <Select
                                value={row.font_family || DEFAULT_FONT}
                                onChange={(v) => updateRow(row.id, 'font_family', v)}
                                optionLabelProp="label"
                              >
                                {FONT_FAMILIES.map((f) => (
                                  <Option key={f.value} value={f.value} label={f.label}>
                                    <span style={{ fontFamily: `'${f.value}', Arial, sans-serif` }}>
                                      {f.label} — <span style={{ color: '#888', fontSize: 12 }}>अ आ इ ई — Aa Bb</span>
                                    </span>
                                  </Option>
                                ))}
                              </Select>
                            </Form.Item>

                            {/* Size, Color, Bold */}
                            <Row gutter={8} style={{ marginBottom: 8 }}>
                              <Col span={8}>
                                <Form.Item label="Font Size (px)" style={{ marginBottom: 0 }}>
                                  <InputNumber
                                    min={12} max={200}
                                    value={row.font_size}
                                    onChange={(v) => updateRow(row.id, 'font_size', v)}
                                    style={{ width: '100%' }}
                                  />
                                </Form.Item>
                              </Col>
                              <Col span={8}>
                                <Form.Item label="Color" style={{ marginBottom: 0 }}>
                                  <input
                                    type="color"
                                    value={row.color}
                                    onChange={(e) => updateRow(row.id, 'color', e.target.value)}
                                    style={{ width: '100%', height: 32, cursor: 'pointer', border: '1px solid #d9d9d9', borderRadius: 4 }}
                                  />
                                </Form.Item>
                              </Col>
                              <Col span={4}>
                                <Form.Item label="Bold" style={{ marginBottom: 0 }}>
                                  <input
                                    type="checkbox"
                                    checked={row.bold}
                                    onChange={(e) => updateRow(row.id, 'bold', e.target.checked)}
                                    style={{ transform: 'scale(1.4)', marginTop: 8 }}
                                  />
                                </Form.Item>
                              </Col>
                              <Col span={4}>
                                <Form.Item label="Align" style={{ marginBottom: 0 }}>
                                  <Select
                                    size="small"
                                    value={row.text_anchor || 'middle'}
                                    onChange={(v) => updateRow(row.id, 'text_anchor', v)}
                                    style={{ width: '100%' }}
                                  >
                                    <Option value="start">Left</Option>
                                    <Option value="middle">Center</Option>
                                    <Option value="end">Right</Option>
                                  </Select>
                                </Form.Item>
                              </Col>
                            </Row>

                            {/* Position — Top %, Left %, nudge arrows */}
                            <Row gutter={8} align="middle">
                              <Col span={6}>
                                <Form.Item label="Top %" style={{ marginBottom: 0 }}>
                                  <InputNumber
                                    min={0} max={97} step={0.5}
                                    value={row.top_pct}
                                    onChange={(v) => updateRow(row.id, 'top_pct', v)}
                                    style={{ width: '100%' }}
                                  />
                                </Form.Item>
                              </Col>
                              <Col span={6}>
                                <Form.Item label="Left %" style={{ marginBottom: 0 }}>
                                  <InputNumber
                                    min={0} max={100} step={0.5}
                                    value={row.left_pct ?? 50}
                                    onChange={(v) => updateRow(row.id, 'left_pct', v)}
                                    style={{ width: '100%' }}
                                  />
                                </Form.Item>
                              </Col>
                              <Col span={12}>
                                <Form.Item label="Nudge (0.5%)" style={{ marginBottom: 0 }}>
                                  <Space size={4}>
                                    <Button size="small" icon={<ArrowUpOutlined />}
                                      onClick={() => updateRow(row.id, 'top_pct', Math.max(0, (row.top_pct || 0) - 0.5))} />
                                    <Button size="small" icon={<ArrowDownOutlined />}
                                      onClick={() => updateRow(row.id, 'top_pct', Math.min(97, (row.top_pct || 0) + 0.5))} />
                                    <Button size="small" icon={<ArrowLeftOutlined />}
                                      onClick={() => updateRow(row.id, 'left_pct', Math.max(0, (row.left_pct ?? 50) - 0.5))} />
                                    <Button size="small" icon={<ArrowRightOutlined />}
                                      onClick={() => updateRow(row.id, 'left_pct', Math.min(100, (row.left_pct ?? 50) + 0.5))} />
                                  </Space>
                                </Form.Item>
                              </Col>
                            </Row>
                          </Card>
                        ))}

                        <Button type="dashed" block icon={<PlusOutlined />} onClick={addRow} style={{ marginBottom: 16 }}>
                          Add Text Row
                        </Button>
                      </Col>

                      {/* Right — drag preview */}
                      <Col xs={24} md={11}>
                        <Text strong style={{ display: 'block', marginBottom: 8 }}>
                          Poster Preview — drag rows / photo to position
                        </Text>
                        {posterPreview ? (
                          <div
                            ref={containerRef}
                            className="festival-preview-container"
                          >
                            <img
                              src={posterPreview}
                              alt="poster"
                              draggable={false}
                              onLoad={handleImgLoad}
                            />

                            {/* Text row overlays */}
                            {rows.map((row, idx) => {
                              const fs = scaledFont(row.font_size);
                              const ff = `'${row.font_family || DEFAULT_FONT}', Arial, sans-serif`;
                              return (
                                <div
                                  key={row.id}
                                  className="festival-drag-label"
                                  title={`Row ${idx + 1} — drag anywhere to reposition`}
                                  style={{
                                    top:        `${row.top_pct}%`,
                                    left:       `${row.left_pct ?? 50}%`,
                                    transform:  (row.text_anchor || 'middle') === 'middle'
                                      ? 'translateX(-50%)'
                                      : (row.text_anchor === 'end' ? 'translateX(-100%)' : 'none'),
                                    fontSize:   `${fs}px`,
                                    color:      row.color,
                                    fontWeight: row.bold ? 'bold' : 'normal',
                                    fontFamily: ff,
                                    lineHeight: 1.25,
                                    textShadow: '0 1px 2px rgba(255,255,255,0.6)',
                                  }}
                                  onMouseDown={(e) => onDragStart(e, row.id)}
                                  onTouchStart={(e) => onDragStart(e, row.id)}
                                >
                                  <span className="festival-drag-handle">⠿</span>
                                  {renderTemplate(row.template, SAMPLE) || `Row ${idx + 1}`}
                                </div>
                              );
                            })}

                            {/* Photo placeholder overlay */}
                            {photoConfig.enabled && (
                              <div
                                title="Member Photo — drag to reposition"
                                style={{
                                  position:      'absolute',
                                  top:           `${photoConfig.top_pct}%`,
                                  left:          `${photoConfig.left_pct}%`,
                                  width:         `${photoSizePct}%`,
                                  paddingBottom: `${photoSizePct}%`,
                                  background:    'rgba(24,144,255,0.22)',
                                  border:        '2px dashed #7a0000',
                                  // Show border ring in preview using box-shadow
                                  boxShadow:     photoConfig.border_enabled
                                    ? `inset 0 0 0 ${Math.max(2, Math.round(photoConfig.border_width * (photoSizePct / 100) * 0.5))}px ${photoConfig.border_color}`
                                    : undefined,
                                  borderRadius:  photoConfig.circle ? '50%' : 6,
                                  cursor:        'grab',
                                  overflow:      'hidden',
                                }}
                                onMouseDown={onPhotoDragStart}
                                onTouchStart={onPhotoDragStart}
                              >
                                <UserOutlined style={{
                                  color:    '#7a0000',
                                  fontSize: 18,
                                  position: 'absolute',
                                  top: '50%', left: '50%',
                                  transform: 'translate(-50%, -50%)',
                                }} />
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="festival-preview-placeholder">
                            Upload a poster image to see the drag preview
                          </div>
                        )}
                        <Text type="secondary" style={{ display: 'block', marginTop: 6, fontSize: 12 }}>
                          Preview uses sample data. Font sizes are proportional to actual poster width ({naturalWidth}px detected).
                        </Text>
                      </Col>
                    </Row>

                    {/* ── Member Photo Placement ────────────────────────── */}
                    <Divider>
                      <Space>
                        <UserOutlined />
                        Member Photo Placement
                        <Tooltip title="Composites the member's profile photo onto the poster. Drag the blue circle in the preview to position it.">
                          <QuestionCircleOutlined style={{ color: '#7a0000' }} />
                        </Tooltip>
                      </Space>
                    </Divider>

                    <Card
                      size="small"
                      style={{ marginBottom: 16, borderColor: photoConfig.enabled ? '#7a0000' : undefined }}
                    >
                      <Row gutter={16} align="middle">
                        <Col xs={24} md={4}>
                          <Form.Item label="Enable Photo" style={{ marginBottom: 0 }}>
                            <Switch
                              checked={photoConfig.enabled}
                              onChange={(v) => setPhotoConfig(prev => ({ ...prev, enabled: v }))}
                              checkedChildren="ON"
                              unCheckedChildren="OFF"
                            />
                          </Form.Item>
                        </Col>
                        {photoConfig.enabled && (
                          <>
                            <Col xs={12} md={3}>
                              <Form.Item label="Top %" style={{ marginBottom: 0 }}>
                                <InputNumber min={0} max={90} step={0.5} value={photoConfig.top_pct}
                                  onChange={(v) => setPhotoConfig(prev => ({ ...prev, top_pct: v }))} style={{ width: '100%' }} />
                              </Form.Item>
                            </Col>
                            <Col xs={12} md={3}>
                              <Form.Item label="Left %" style={{ marginBottom: 0 }}>
                                <InputNumber min={0} max={90} step={0.5} value={photoConfig.left_pct}
                                  onChange={(v) => setPhotoConfig(prev => ({ ...prev, left_pct: v }))} style={{ width: '100%' }} />
                              </Form.Item>
                            </Col>
                            <Col xs={12} md={3}>
                              <Form.Item label="Size % (of poster)" style={{ marginBottom: 0 }}>
                                <InputNumber min={5} max={50} step={1} value={photoConfig.size_pct}
                                  onChange={(v) => setPhotoConfig(prev => ({ ...prev, size_pct: v }))} style={{ width: '100%' }} />
                              </Form.Item>
                            </Col>
                            <Col xs={12} md={3}>
                              <Form.Item label="Circle" style={{ marginBottom: 0 }}>
                                <input type="checkbox" checked={photoConfig.circle}
                                  onChange={(e) => setPhotoConfig(prev => ({ ...prev, circle: e.target.checked }))}
                                  style={{ transform: 'scale(1.4)', marginTop: 8 }} />
                              </Form.Item>
                            </Col>
                            {/* ── Border controls ── */}
                            <Col xs={12} md={3}>
                              <Form.Item label="Border" style={{ marginBottom: 0 }}>
                                <Switch
                                  size="small"
                                  checked={photoConfig.border_enabled}
                                  onChange={(v) => setPhotoConfig(prev => ({ ...prev, border_enabled: v }))}
                                  checkedChildren="ON"
                                  unCheckedChildren="OFF"
                                />
                              </Form.Item>
                            </Col>
                            {photoConfig.border_enabled && (
                              <>
                                <Col xs={12} md={3}>
                                  <Form.Item label="Border Width (px)" style={{ marginBottom: 0 }}>
                                    <InputNumber
                                      min={1} max={40} step={1}
                                      value={photoConfig.border_width}
                                      onChange={(v) => setPhotoConfig(prev => ({ ...prev, border_width: v }))}
                                      style={{ width: '100%' }}
                                    />
                                  </Form.Item>
                                </Col>
                                <Col xs={12} md={2}>
                                  <Form.Item label="Border Color" style={{ marginBottom: 0 }}>
                                    <input
                                      type="color"
                                      value={photoConfig.border_color || '#ffffff'}
                                      onChange={(e) => setPhotoConfig(prev => ({ ...prev, border_color: e.target.value }))}
                                      style={{ width: '100%', height: 32, cursor: 'pointer', border: '1px solid #d9d9d9', borderRadius: 4 }}
                                    />
                                  </Form.Item>
                                </Col>
                              </>
                            )}
                          </>
                        )}
                      </Row>
                      {photoConfig.enabled && (
                        <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
                          The member's profile photo will be composited at the specified position.
                          Drag the blue circle in the preview to reposition. Size % is relative to poster width.
                        </Text>
                      )}
                    </Card>
                  </>
                )}

                <Divider />
                <Space>
                  <Button icon={<SaveOutlined />} htmlType="submit" loading={saving} size="large" style={{ background: '#7a0000', borderColor: '#7a0000', color: '#fff' }}>
                    {isEdit ? 'Update Campaign' : 'Save Campaign'}
                  </Button>
                  <Button size="large" onClick={() => history.push('/festival-campaign')}>Cancel</Button>
                </Space>
              </Form>
            </Card>
          </Col>
        </Row>
      </Spin>
    </div>
  );
}
